import { HTMLAttributes, memo } from 'react';
import { cn } from '@/lib/utils';

export type BadgeVariant = 'default' | 'success' | 'error' | 'warning' | 'info' | 'primary' | 'secondary';

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant;
}

/**
 * Badge (memoized) - Etiqueta con estilos Tailwind Plus
 * 
 * Mejoras aplicadas:
 * - Ring inset para borde sutil
 * - Dark mode: bg-color-400/10 text-color-400
 * - Ring con opacidad para dark mode
 */
const variantClasses: Record<BadgeVariant, string> = {
  default: cn(
    'bg-secondary-50 text-secondary-600',
    'ring-1 ring-inset ring-secondary-500/10',
    'dark:bg-secondary-400/10 dark:text-secondary-400 dark:ring-secondary-400/20'
  ),
  
  success: cn(
    'bg-success-50 text-success-700',
    'ring-1 ring-inset ring-success-600/20',
    'dark:bg-success-400/10 dark:text-success-400 dark:ring-success-500/20'
  ),
  
  error: cn(
    'bg-danger-50 text-danger-700',
    'ring-1 ring-inset ring-danger-600/10',
    'dark:bg-danger-400/10 dark:text-danger-400 dark:ring-danger-400/20'
  ),
  
  warning: cn(
    'bg-warning-50 text-warning-800',
    'ring-1 ring-inset ring-warning-600/20',
    'dark:bg-warning-400/10 dark:text-warning-500 dark:ring-warning-400/20'
  ),
  
  info: cn(
    'bg-info-50 text-info-700',
    'ring-1 ring-inset ring-info-700/10',
    'dark:bg-info-400/10 dark:text-info-400 dark:ring-info-400/30'
  ),
  
  primary: cn(
    'bg-primary-50 text-primary-700',
    'ring-1 ring-inset ring-primary-700/10',
    'dark:bg-primary-400/10 dark:text-primary-400 dark:ring-primary-400/30'
  ),
  
  secondary: cn(
    'bg-secondary-50 text-secondary-600',
    'ring-1 ring-inset ring-secondary-500/10',
    'dark:bg-secondary-400/10 dark:text-secondary-400 dark:ring-secondary-400/20'
  ),
};

export const Badge = memo(function Badge({ variant = 'default', className = '', children, ...props }: BadgeProps) {
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-md px-2 py-1 text-xs font-medium',
        variantClasses[variant],
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
});
